import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BatteryCharging, Droplets, Fan, FlaskConical, Flame, Link2, Play, Thermometer } from 'lucide-react';
import { TIPI_GENERATORE, type TipoGeneratore } from '@emacomfort/domain';
import {
  CONFIGURAZIONE_PREDEFINITA,
  MAX_ZONE,
  aQuery,
  daQuery,
  impiantoParametrico,
  type ConfigurazioneSimulata,
  type ModoCentralina,
  type ModoFotovoltaico,
} from '@emacomfort/device-client';
import { useSimulatore } from '@/app/simulatore';
import { Avviso, Badge, Bottone, Campo, Card, Interruttore, Pagina, Segmenti, Select, stili } from '@/ui';
import s from '../features.module.css';

/**
 * Il configuratore del simulatore.
 *
 * A cosa serve: provare l'app su una configurazione d'impianto che non è
 * installata da nessuna parte. Fino ad ora le uniche configurazioni
 * verificabili erano le tre fixture, e chiunque volesse vedere "sei zone senza
 * sanitario" o "sistema di terzi in sola lettura" doveva scrivere codice.
 *
 * La configurazione sta nell'URL. `/simulatore?zone=6&acs=0&centralina=terze_parti`
 * apre esattamente quella configurazione, quindi una configurazione da
 * riprodurre — in una segnalazione, in una prova, in una dimostrazione — è un
 * link e non un elenco di passi da rifare a mano.
 *
 * 🚨 Questa pagina mostra dati SIMULATI, sempre e ovunque. Su una macchina con
 * la build configurata l'app parte sui dati veri: entrare qui li sostituisce
 * per la sessione, e la pagina lo dice a chiare lettere — una demo scambiata
 * per l'impianto vero è il modo esatto in cui un guasto passa inosservato.
 */
export function SimulatorePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const avvia = useSimulatore((st) => st.avvia);
  const attivo = useSimulatore((st) => st.attivo);
  const esci = useSimulatore((st) => st.esci);

  // L'URL è la fonte: si arriva qui con una configurazione già scritta nel
  // link, oppure con nessun parametro e allora vale quella predefinita.
  const [config, setConfig] = useState<ConfigurazioneSimulata>(() => daQuery(params));

  // Ogni modifica si riscrive nell'URL (senza aggiungere voci alla cronologia,
  // o il tasto "indietro" dovrebbe ripercorrere ogni singolo interruttore).
  useEffect(() => {
    setParams(aQuery(config), { replace: true });
  }, [config, setParams]);

  const anteprima = useMemo(() => impiantoParametrico(config), [config]);
  const modifica = (patch: Partial<ConfigurazioneSimulata>) => setConfig((c) => ({ ...c, ...patch }));

  const apri = () => {
    avvia(config);
    navigate('/pannello');
  };

  const link = `${window.location.origin}/simulatore?${aQuery(config).toString()}`;
  const [copiato, setCopiato] = useState(false);
  const copia = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiato(true);
      window.setTimeout(() => setCopiato(false), 2000);
    } catch {
      /* clipboard negata: il link resta visibile e selezionabile a mano */
    }
  };

  return (
    <Pagina
      titolo={t('simulatore.titolo')}
      sottotitolo={t('simulatore.sottotitolo')}
      azione={<Badge tono="warn">{t('simulatore.datiSimulati')}</Badge>}
    >
      <Avviso tono="warn">{t('simulatore.avvertenza')}</Avviso>

      <div className={s.grigliaDue}>
        <Card titolo={t('simulatore.impianto')} icona={<Thermometer size={20} aria-hidden />}>
          <Campo etichetta={t('simulatore.centralina')} nota={t('simulatore.centralinaNota')}>
            <Segmenti
              etichetta={t('simulatore.centralina')}
              valore={config.centralina}
              onChange={(v: ModoCentralina) => modifica({ centralina: v })}
              opzioni={[
                { valore: 'emacomfort', etichetta: t('simulatore.centralinaPropria') },
                { valore: 'terze_parti', etichetta: t('simulatore.centralinaTerzi') },
              ]}
            />
          </Campo>
          <Campo etichetta={t('simulatore.zone')}>
            <Select
              value={String(config.zone)}
              onChange={(e) => modifica({ zone: Number(e.target.value) })}
              aria-label={t('simulatore.zone')}
            >
              {Array.from({ length: MAX_ZONE }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {t('simulatore.zoneConteggio', { count: n })}
                </option>
              ))}
            </Select>
          </Campo>
        </Card>

        <Card titolo={t('simulatore.generatori')} icona={<Flame size={20} aria-hidden />}>
          <p className={s.letturaNota}>{t('simulatore.generatoriNota')}</p>
          {TIPI_GENERATORE.map((tipo) => (
            <div key={tipo} className={s.impostazione}>
              <span>{t(`generatore.${tipo}`)}</span>
              <Interruttore
                acceso={config.generatori.includes(tipo)}
                onChange={(v) => modifica({ generatori: cambiaGeneratore(config.generatori, tipo, v) })}
                etichetta={t(`generatore.${tipo}`)}
                testoAcceso={t('comune.si')}
                testoSpento={t('comune.no')}
              />
            </div>
          ))}
        </Card>

        <Card titolo={t('simulatore.servizi')} icona={<Droplets size={20} aria-hidden />}>
          <Riga
            etichetta={t('simulatore.sanitario')}
            acceso={config.sanitario}
            onChange={(v) => modifica({ sanitario: v })}
          />
          <Riga etichetta={t('simulatore.vmc')} acceso={config.vmc} onChange={(v) => modifica({ vmc: v })} />
          <Riga
            etichetta={t('simulatore.sondaPuffer')}
            acceso={config.sondaPuffer}
            onChange={(v) => modifica({ sondaPuffer: v })}
          />
        </Card>

        <Card titolo={t('simulatore.sonde')} icona={<Fan size={20} aria-hidden />}>
          <p className={s.letturaNota}>{t('simulatore.sondeNota')}</p>
          <Riga
            etichetta={t('simulatore.sondaEsterna')}
            acceso={config.sondaEsterna}
            onChange={(v) => modifica({ sondaEsterna: v })}
          />
          <Riga etichetta={t('simulatore.umidita')} acceso={config.umidita} onChange={(v) => modifica({ umidita: v })} />
          <Riga
            etichetta={t('simulatore.sondeCentrale')}
            acceso={config.sondeCentrale}
            onChange={(v) => modifica({ sondeCentrale: v })}
          />
        </Card>

        <Card titolo={t('simulatore.fotovoltaico')} icona={<BatteryCharging size={20} aria-hidden />}>
          <Segmenti
            etichetta={t('simulatore.fotovoltaico')}
            valore={config.fotovoltaico}
            onChange={(v: ModoFotovoltaico) => modifica({ fotovoltaico: v })}
            opzioni={[
              { valore: 'assente', etichetta: t('simulatore.fvAssente') },
              { valore: 'senza_batteria', etichetta: t('simulatore.fvSenzaBatteria') },
              { valore: 'con_batteria', etichetta: t('simulatore.fvConBatteria') },
            ]}
          />
        </Card>

        <Card titolo={t('simulatore.riepilogo')} icona={<FlaskConical size={20} aria-hidden />}>
          <dl className={stili.griglia} style={{ gridTemplateColumns: 'auto 1fr', rowGap: 6, columnGap: 16 }}>
            <dt className={stili.muto}>{t('simulatore.zone')}</dt>
            <dd>{anteprima.zone.length}</dd>
            <dt className={stili.muto}>{t('simulatore.canali')}</dt>
            <dd>{anteprima.canali.length}</dd>
            <dt className={stili.muto}>{t('simulatore.generatori')}</dt>
            <dd>{anteprima.generatori.map((g) => g.nome).join(', ')}</dd>
            <dt className={stili.muto}>{t('simulatore.comandi')}</dt>
            <dd>
              {anteprima.sorgenti.some((so) => so.comandabile)
                ? t('simulatore.comandiSi')
                : t('simulatore.comandiNo')}
            </dd>
          </dl>
        </Card>
      </div>

      <Card titolo={t('simulatore.link')} icona={<Link2 size={20} aria-hidden />}>
        <p className={s.letturaNota}>{t('simulatore.linkNota')}</p>
        <code className={s.letturaNota} style={{ wordBreak: 'break-all' }}>
          {link}
        </code>
        <div className={stili.riga} style={{ gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          <Bottone variante="primario" onClick={apri}>
            <span className={stili.riga}>
              <Play size={20} aria-hidden />
              {t('simulatore.apri')}
            </span>
          </Bottone>
          <Bottone variante="fantasma" onClick={() => void copia()}>
            {copiato ? t('simulatore.copiato') : t('simulatore.copia')}
          </Bottone>
          <Bottone variante="fantasma" onClick={() => setConfig(CONFIGURAZIONE_PREDEFINITA)}>
            {t('simulatore.ripristina')}
          </Bottone>
          {attivo && (
            <Bottone
              variante="fantasma"
              onClick={() => {
                esci();
                navigate('/pannello');
              }}
            >
              {t('simulatore.esci')}
            </Bottone>
          )}
        </div>
      </Card>
    </Pagina>
  );
}

function Riga({ etichetta, acceso, onChange }: { etichetta: string; acceso: boolean; onChange(v: boolean): void }) {
  const { t } = useTranslation();
  return (
    <div className={s.impostazione}>
      <span>{etichetta}</span>
      <Interruttore
        acceso={acceso}
        onChange={onChange}
        etichetta={etichetta}
        testoAcceso={t('comune.si')}
        testoSpento={t('comune.no')}
      />
    </div>
  );
}

/** Aggiunge o toglie un generatore mantenendo l'ordine di priorità dichiarato
 *  da `TIPI_GENERATORE`: la cascata è ordinata, non un insieme. */
function cambiaGeneratore(attuali: TipoGeneratore[], tipo: TipoGeneratore, acceso: boolean): TipoGeneratore[] {
  const insieme = new Set(attuali);
  if (acceso) insieme.add(tipo);
  else insieme.delete(tipo);
  return TIPI_GENERATORE.filter((x) => insieme.has(x));
}
